/**
 * PhotoEditorCanvasSizable Component
 * Feature: 043-critique-edit-suggestions
 *
 * A wrapper around PhotoEditorCanvas that automatically calculates width and height
 * based on its container size and crop aspect ratio. This component extracts the
 * sizing logic that was previously duplicated in EditPreviewRenderer and EditPreviewComparison.
 *
 * Usage:
 * - Size the component using CSS (width/height, flex, grid, etc.)
 * - The component measures itself and calculates appropriate canvas dimensions
 * - Handles crop aspect ratio preservation automatically
 * - Re-calculates on resize using ResizeObserver
 */

import React from 'react'
import { PhotoEditorCanvas } from './PhotoEditorCanvas'
import type { PhotoEditorCanvasProps } from './PhotoEditorCanvas'

export interface PhotoEditorCanvasSizableProps extends Omit<PhotoEditorCanvasProps, 'width' | 'height'> {
  /** Additional CSS class names for the container */
  className?: string
  /** Additional inline styles for the container */
  style?: React.CSSProperties
  /** Fallback dimensions when container hasn't been measured yet */
  fallbackWidth?: number
  fallbackHeight?: number
  /** Original image dimensions (required for free aspect ratio crops) */
  originalImageWidth?: number
  originalImageHeight?: number
  /** Enable debug overlay showing renderer and dimensions */
  showDebug?: boolean
}

/**
 * PhotoEditorCanvasSizable - Self-sizing wrapper for PhotoEditorCanvas
 *
 * This component:
 * 1. Measures its container size using ResizeObserver
 * 2. Calculates canvas dimensions based on crop aspect ratio (if present)
 * 3. Ensures canvas fits within container while preserving aspect ratio
 * 4. Passes calculated width/height to PhotoEditorCanvas
 *
 * @example
 * // In a flex container
 * <div className="flex-1 flex items-center justify-center">
 *   <PhotoEditorCanvasSizable
 *     imageUrl={photo.url}
 *     cropData={cropData}
 *     lightData={lightData}
 *     className="rounded"
 *   />
 * </div>
 *
 * @example
 * // With explicit sizing
 * <PhotoEditorCanvasSizable
 *   imageUrl={photo.url}
 *   exposure={0.5}
 *   className="w-full h-full max-w-[800px] max-h-[600px]"
 * />
 */
export function PhotoEditorCanvasSizable({
  className = '',
  style = {},
  fallbackWidth = 800,
  fallbackHeight = 600,
  originalImageWidth,
  originalImageHeight,
  showDebug = false,
  cropData,
  ...canvasProps
}: PhotoEditorCanvasSizableProps): JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = React.useState<{ width: number; height: number } | null>(null)
  const lastSizeRef = React.useRef<{ width: number; height: number } | null>(null)
  const resizeTimeoutRef = React.useRef<number | null>(null)

  // Calculate canvas dimensions that fit in container and match crop aspect ratio
  const canvasDimensions = React.useMemo(() => {
    if (!containerSize) {
      // Use fallback dimensions when container hasn't been measured yet
      return { width: fallbackWidth, height: fallbackHeight }
    }

    // If we have crop data, use its aspect ratio
    if (cropData) {
      // Determine the aspect ratio to use for sizing calculations
      let cropAspect: number

      // Priority 1: Use stored computedAspectRatio if available (most reliable)
      if (cropData.computedAspectRatio) {
        cropAspect = cropData.computedAspectRatio
      }
      // Priority 2: Parse fixed aspect ratio strings (e.g., "65:24")
      else if (cropData.aspectRatio && cropData.aspectRatio !== 'free') {
        const [widthRatio, heightRatio] = cropData.aspectRatio.split(':').map(Number)
        cropAspect = widthRatio / heightRatio
      }
      // Priority 3: For free crops without computedAspectRatio, try to calculate from image dimensions
      else if (cropData.aspectRatio === 'free' && originalImageWidth && originalImageHeight) {
        const cropPixelWidth = (cropData.width / 100) * originalImageWidth
        const cropPixelHeight = (cropData.height / 100) * originalImageHeight
        cropAspect = cropPixelWidth / cropPixelHeight
      }
      // Fallback: Use percentage ratios (inaccurate for near-100% crops)
      else {
        cropAspect = cropData.width / cropData.height
      }

      const containerAspect = containerSize.width / containerSize.height

      let canvasWidth: number
      let canvasHeight: number

      if (cropAspect > containerAspect) {
        // Crop is wider than container - fit to width
        canvasWidth = containerSize.width
        canvasHeight = containerSize.width / cropAspect
      } else {
        // Crop is taller than container - fit to height
        canvasHeight = containerSize.height
        canvasWidth = containerSize.height * cropAspect
      }

      // Final safety check: ensure both dimensions fit within container bounds
      if (canvasWidth > containerSize.width) {
        const scale = containerSize.width / canvasWidth
        canvasWidth = containerSize.width
        canvasHeight = canvasHeight * scale
      }
      if (canvasHeight > containerSize.height) {
        const scale = containerSize.height / canvasHeight
        canvasHeight = containerSize.height
        canvasWidth = canvasWidth * scale
      }

      return {
        width: Math.floor(canvasWidth),
        height: Math.floor(canvasHeight),
      }
    }

    // No crop data - use container dimensions
    return containerSize
  }, [containerSize, cropData, fallbackWidth, fallbackHeight, originalImageWidth, originalImageHeight])

  // Measure container size and update canvas dimensions
  React.useEffect(() => {
    if (!containerRef.current) return

    // Measurement function
    const measureContainer = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const newWidth = Math.floor(rect.width)
        const newHeight = Math.floor(rect.height)

        // Only update if dimensions actually changed (prevent resize loops)
        if (newWidth > 0 && newHeight > 0) {
          const hasChanged = !lastSizeRef.current ||
            lastSizeRef.current.width !== newWidth ||
            lastSizeRef.current.height !== newHeight

          if (hasChanged) {
            const newSize = { width: newWidth, height: newHeight }
            lastSizeRef.current = newSize
            setContainerSize(newSize)
          }
        }
      }
    }

    // Initial measurement after a tick to let flex/grid layout resolve
    const timeoutId = setTimeout(measureContainer, 0)

    // Debounced resize handler
    const debouncedMeasure = () => {
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current)
      }
      resizeTimeoutRef.current = window.setTimeout(measureContainer, 16) // ~60fps
    }

    const resizeObserver = new ResizeObserver(() => {
      debouncedMeasure()
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      clearTimeout(timeoutId)
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current)
      }
      resizeObserver.disconnect()
    }
  }, [])

  // Container style: combine user styles with positioning requirements
  // Important: Container must have a defined size (via width/height or by being in a sized parent)
  // to avoid measurement loops. We don't set display: inline-flex by default because that
  // makes the container try to fit its content, which causes the shrinking loop.
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    // User styles override defaults
    ...style,
  }

  return (
    <div ref={containerRef} className={`${className} h-full w-full overflow-hidden`} style={containerStyle}>
      {containerSize ? (
        <PhotoEditorCanvas
          {...canvasProps}
          cropData={cropData}
          width={canvasDimensions.width}
          height={canvasDimensions.height}
          showDebug={showDebug}
        />
      ) : null}
    </div>
  )
}
